import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { Helmet } from 'react-helmet';
import './ActivateAccount.css';

const ActivateAccount: React.FC = () => {
  const { authState } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(authState.user?.email || null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    if (!userEmail) {
      // Try to get email from Supabase user
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user?.email) {
          setUserEmail(user.email);
        }
      });
    }
  }, [userEmail]);

  const handleResend = async () => {
    setStatus(null);
    setLoading(true);
    try {
      let emailToUse = userEmail;
      if (!emailToUse) {
        // Try to get email again
        const {
          data: { user },
        } = await supabase.auth.getUser();
        emailToUse = user?.email || null;
      }
      if (!emailToUse) {
        setStatus('No email found for your account. Please log in again.');
        setLoading(false);
        return;
      }
      // Supabase v2: resend using signUp with same email
      const { error } = await supabase.auth.signUp({ email: emailToUse, password: 'dummy' });
      if (error && !error.message.toLowerCase().includes('user already registered')) {
        setStatus('Error resending activation email: ' + error.message);
      } else {
        setStatus('Activation email sent! Please check your inbox.');
      }
    } catch (err: any) {
      setStatus('Error resending activation email.');
    } finally {
      setLoading(false);
    }
  };

  const isSuccessStatus = status?.startsWith('Activation email sent') ?? false;

  return (
    <div className={`activate-page ${visible ? 'activate-page--visible' : ''}`}>
      <Helmet>
        <title>DHH Collab | Activate Account</title>
        <meta name="description" content="Activate your DHH Collab account to start collaborating on music." />
      </Helmet>

      <div className="activate-page__bg" aria-hidden>
        <span className="activate-page__blob activate-page__blob--1" />
        <span className="activate-page__blob activate-page__blob--2" />
        <span className="activate-page__grid" />
      </div>

      <div className="activate-page__inner container-padding">
        <div className="activate-page__layout">
          <aside className="activate-page__visual" aria-hidden>
            <span className="activate-page__eyebrow">Almost There</span>
            <h1 className="activate-page__title">Verify Account</h1>
            <p className="activate-page__lead">
              One click in your inbox unlocks the full DHH Collab experience — beats, verses, and collabs await.
            </p>

            <div className="activate-page__graphic">
              <div className="activate-page__radar">
                <span className="activate-page__radar-ring" />
                <span className="activate-page__radar-ring activate-page__radar-ring--2" />
                <span className="activate-page__radar-ring activate-page__radar-ring--3" />
              </div>
              <div className="activate-page__shield">
                <span className="activate-page__check" />
              </div>
              <div className="activate-page__mail">
                <div className="activate-page__mail-body" />
                <div className="activate-page__mail-flap" />
              </div>
              <span className="activate-page__dot activate-page__dot--1" />
              <span className="activate-page__dot activate-page__dot--2" />
            </div>
          </aside>

          <div className="activate-page__card">
            <h2 className="activate-page__card-title">Activate Your Account</h2>
            <p className="activate-page__card-text">
              A confirmation email has been sent to your registered email address.
              <br />
              Please check your inbox and click the activation link to complete your registration.
            </p>
            <p className="activate-page__card-hint">
              Didn&apos;t receive the email? Check your spam folder or resend below.
            </p>

            <button
              type="button"
              onClick={handleResend}
              disabled={loading}
              className={`activate-page__submit ${loading ? 'activate-page__submit--disabled' : 'activate-page__submit--ready'}`}
            >
              {loading ? 'Resending...' : 'Resend Email'}
            </button>

            {status && (
              <div
                className={`activate-page__status ${
                  isSuccessStatus ? 'activate-page__status--success' : 'activate-page__status--error'
                }`}
              >
                {status}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateAccount;
